import axios from '~/utils/axios';
import { filtersToParams } from '~/utils/filtersToParams';
import { urlWithParams } from '~/utils';
import { Account, Limit } from '~/types';

const buildAccountsURL = (
  resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts',
  profileId?: string
) => {
  if (resource === 'tenant/accounts') {
    return `tenant/accounts`;
  } else if (!profileId) {
    return `${resource}/accounts`;
  } else {
    return `${resource}/${profileId}/accounts`;
  }
};

export const getAccounts = (
  resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts',
  profileId?: string,
  pagination?: { page: number; perPage: number },
  filters?: { [x: string]: any }
) => {
  const params = filtersToParams(filters);
  if (pagination) {
    params.append('page', pagination.page.toString());
    params.append('perPage', pagination.perPage.toString());
  }
  const url = buildAccountsURL(resource, profileId);
  return axios
    .get<Account[]>(url, { params })
    .then((res) => res.data);
};

export const getAccountsWithHeaders = (
  resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts',
  profileId?: string,
  pagination?: { page: number; perPage: number },
  filters?: { [x: string]: any }
) => {
  const params = filtersToParams(filters);
  if (pagination) {
    params.append('page', pagination.page.toString());
    params.append('perPage', pagination.perPage.toString());
  }
  const url = buildAccountsURL(resource, profileId);
  return axios.get(url, { params });
};

const buildAccountActionURL = (
  accountId: string,
  accountType: 'accounts' | 'externalSources' | 'tenant/accounts',
  action: 'block' | 'unblock' | 'close'
) => {
  if (accountType === 'tenant/accounts') {
    return `/tenant/accounts/${accountId}/${action}`;
  }
  return `/${accountType}/${accountId}/${action}`;
};

export const blockAccount = (
  accountId: string,
  accountType: 'accounts' | 'externalSources' | 'tenant/accounts',
  note: string
) => {
  return axios.post(buildAccountActionURL(accountId, accountType, 'block'), { note });
};

export const unblockAccount = (
  accountId: string,
  accountType: 'accounts' | 'externalSources' | 'tenant/accounts',
  note: string
) => {
  return axios.post(buildAccountActionURL(accountId, accountType, 'unblock'), { note });
};

export const closeAccount = (
  accountId: string,
  accountType: 'accounts' | 'externalSources' | 'tenant/accounts',
  note: string
) => {
  return axios.post(buildAccountActionURL(accountId, accountType, 'close'), { note });
};

export const archiveAffiliatedObject = (
  resource: string,
  resourceId: string,
  referenceId: string
) => {
  return axios.delete(`/${resource}/${resourceId}/references/${referenceId}`);
};

export const getAccount = (accountId: string) => {
  return axios.get<Account>(`/accounts/${accountId}`).then((res) => res.data);
};

export const getAccountsLimits = (accountId: string) => {
  return axios.get<Limit[]>(`/accounts/${accountId}/limits`).then((res) => res.data);
};

export const getProfileLimits = (profileId: string) => {
  return axios.get<Limit[]>(`/profiles/${profileId}/limits`).then((res) => res.data);
};

interface getBankDaySummaryProps {
  id: string;
  next?: string | null;
  filters?: { [x: string]: any };
}

export type BankDaySummaryType = {
  accountId: string;
  code: string;
  date: string;
  amount: number;
  count: number;
};

export const getBankDaySummary = (props: getBankDaySummaryProps) => {
  const { id, filters, next } = props;
  const resultURL = `tenant/accounts/${id}/summary`;

  return axios.get<BankDaySummaryType[]>(
    urlWithParams(resultURL, { next, ...filters }, 'YYYY-MM-DD')
  );
};

export const resetAccountError = (props: { accountId: string; comment: string }) => {
  const { accountId, comment } = props;
  return axios.post(`accounts/${accountId}/resetError`, { comment });
};

export type AccountSearchRes = {
  cursors: {
    next?: string;
    prev?: string;
  };
  accounts: Account[];
};

export interface SearchAccountsParams {
  next?: string | null;
  limit?: number;
  number?: string;
  type?: string;
  currency?: string;
  status?: string;
  withClosed?: boolean;
  withError?: boolean;
  glType?: string;
  regulatoryType?: string;
  profileType?: string;
  profileId?: string;
}

export const searchAccounts = (params: SearchAccountsParams) => {
  return axios.get<AccountSearchRes>(urlWithParams('/accounts', params));
};
